import React, { useRef } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { msgid, ngettext, t } from 'ttag'
import { ReactComponent as RemoveIcon } from '../../../../assets/v3/icons/remove.svg'
import { ReactComponent as AddRoundedIcon } from '../../../../assets/v3/icons/add-rounded.svg'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import { Box, IconButton, TextField } from '@material-ui/core'
import { FormData } from '../../ChallengeAdmin'
import { activityTypes } from '../../activityTypes'
import { CustomTypeCategory, customUnits } from '../../../../services/CustomTypes'
import ChallengeTotalPointsChallengeField from './ChallengeTotalPointsChallengeField'
import { CompanyId, CustomType, GoalId, GoalWithProgress, ProgressType } from '../../../../services/api.types'
import { isSimpleDayType } from '../../../../common/helpers/challenges'
import { darkBlue } from '../../../../styles/material-ui-theme-v2'
import { isRtl } from '../../../../i18nInit'
import { convertUTCToLocalDate } from '../../../../common/helpers/date'
import dayjs from '../../../../dayjsConfig'
import Typography from '@material-ui/core/Typography'

interface ChallengeTotalPointsProps {
  control: Control<FormData, any>
  watch: UseFormWatch<FormData>
  register: UseFormRegister<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  goals: GoalWithProgress[]
  companyId: CompanyId
  goalId?: GoalId
}

export interface TotalPointsLogic {
  id: GoalId
}

const getNextNotSelectedChallenge = (fields: any[], relevantChallenges: GoalWithProgress[]) => {
  console.log('fields', fields)
  const unusedChallenges: any[] = relevantChallenges.filter(
    (challenge) => !fields.some((field) => field.id === challenge.id),
  )
  console.log('unusedChallenges', unusedChallenges)
  return unusedChallenges[0]?.id || null
}

function filterChallengesFullyContained(
  companyId: CompanyId,
  challenges: GoalWithProgress[],
  startDate?: Date,
  endDate?: Date,
): GoalWithProgress[] {
  if (!startDate || !endDate) return []
  const startMs = startDate.getTime()
  const endMs = dayjs(endDate).endOf('day').valueOf()
  // console.log('startDate:', startDate, '| endDate:', endDate, endMs)
  return challenges
    .filter((challenge) => {
      /*if (challenge.companyId === companyId && challenge.customType === CustomType.POINTS)
        console.log(
          challenge.title,
          new Date(challenge.startDate),
          '|',
          new Date(challenge.endDate),
          convertUTCToLocalDate(new Date(challenge.endDate)).getTime(),
          dayjs(convertUTCToLocalDate(new Date(challenge.endDate))).endOf('day').valueOf(),
        )*/
      return (
        challenge.companyId === companyId &&
        challenge.customType === CustomType.POINTS &&
        convertUTCToLocalDate(new Date(challenge.startDate)).getTime() >= startMs &&
        dayjs(convertUTCToLocalDate(new Date(challenge.endDate)))
          .endOf('day')
          .valueOf() <= endMs
      )
    })
    .sort((a, b) => a.startDate - b.startDate)
}

export default function ChallengeTotalPoints({
  control,
  setValue,
  watch,
  errors,
  goals,
  companyId,
  goalId,
}: ChallengeTotalPointsProps) {
  const { fields, append, move, remove, update } = useFieldArray({
    control,
    name: 'challengeTotalPoints',
    keyName: '_id',
  })
  const childrenRef = useRef<Record<string, (sourceIndex: number, destIndex: number) => void>>({})

  const dateRange = watch('dateRange')
  const [relevantChallenges, setRelevantChallenges] = React.useState([] as GoalWithProgress[])

  const isFirstRun = React.useRef(true)
  React.useEffect(() => {
    if (companyId && goals && dateRange && dateRange[0] && dateRange[1]) {
      const relevantChallenges = filterChallengesFullyContained(companyId, goals, dateRange[0], dateRange[1])
      setRelevantChallenges(relevantChallenges)

      // console.log('new relevantChallenges', relevantChallenges)
      if (isFirstRun.current) {
        isFirstRun.current = false

        let fieldsCount = !fields ? 0 : fields.length

        if (relevantChallenges && relevantChallenges.length > 0) {
          // If not empty, check if all challenges are valid.
          if (fields && fields.length > 0) {
            for (let i = fields.length - 1; i >= 0; i--) {
              const field = fields[i]
              const isRelevant = relevantChallenges.some((challenge) => challenge.id === field.id)
              // console.log('field.id:', field.id, 'isRelevant', isRelevant)
              if (!isRelevant) {
                // console.log('remove index:', i)
                remove(i)
                fieldsCount--
              }
            }
          }

          // If empty, add challenges
          if (fieldsCount <= 0) {
            // console.log('Empty challengeTotalPoints | add all relevant challenges')
            relevantChallenges.forEach((challenge) => append({ id: challenge.id }))
          }
        } else {
          // console.log('relevantChallenges is empty | remove all')
          for (let i = fields.length - 1; i >= 0; i--) {
            // console.log('remove index:', i)
            remove(i)
          }
        }
      }
    }
  }, [companyId, goals, dateRange])

  const reorder = (result: any) => {
    const { source, destination, type } = result
    if (!destination) {
      return
    }
    const sourceIndex = source.index
    const destIndex = destination.index

    if (type === 'parentContainer') {
      move(sourceIndex, destIndex)
    } else if (type === 'childContainer' && source.droppableId) {
      const reorderChild = childrenRef.current[source.droppableId]
      if (reorderChild) {
        reorderChild(sourceIndex, destIndex)
      }
    }
  }

  const handleAdd = () => {
    const id = getNextNotSelectedChallenge(fields, relevantChallenges)
    append({
      id,
    })
  }

  // Hack to re-render form. Refactor if you know any better way.
  const onChallengeChange = (index: number, event: any) => {
    move(0, 0)
  }

  return relevantChallenges && relevantChallenges.length > 0 ? (
    <>
      <DragDropContext onDragEnd={reorder}>
        <Droppable droppableId="parent" type="parentContainer">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ listStyle: 'none', padding: 0, margin: 0 }}
            >
              {fields.map((item, index) => {
                return (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(provided, { isDragging }) => (
                      <li ref={provided.innerRef} {...provided.draggableProps}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 10,
                          }}
                        >
                          <div style={{ paddingTop: 15 }} {...provided.dragHandleProps}>
                            {index + 1}.
                          </div>
                          {/*<div
                            className="DragHandle"
                            style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', height: '50px' }}
                            {...provided.dragHandleProps}
                          >
                            <DragHandleIcon />
                          </div>*/}
                          <div style={{ flexGrow: 1 }}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                /*marginRight: isRtl ? 0 : fields.length == 1 ? 48 : 0,
                                marginLeft: !isRtl ? 0 : fields.length == 1 ? 48 : 0,*/
                              }}
                            >
                              <ChallengeTotalPointsChallengeField
                                name={`challengeTotalPoints.${index}.id`}
                                onChallengeChange={onChallengeChange}
                                fields={fields}
                                index={index}
                                relevantChallenges={relevantChallenges}
                                {...{ control }}
                              />

                              {/*{fields.length > 1 && (*/}
                              <IconButton
                                style={{
                                  flexShrink: 0,
                                  marginBottom: '10px',
                                  position: 'relative',
                                  right: isRtl ? 'unset' : '-10px',
                                  left: !isRtl ? 'unset' : '-10px',
                                }}
                                title={t`Remove`}
                                aria-label={t`Remove`}
                                onClick={() => remove(index)}
                              >
                                <RemoveIcon
                                  style={{
                                    width: 24,
                                    height: 24,
                                  }}
                                />
                              </IconButton>
                              {/*)}*/}
                            </div>
                          </div>
                        </div>

                        {/*<div
                          style={{
                            padding: '6px 0 16px',
                          }}
                        >
                          <div
                            style={{
                              height: '1px',
                              backgroundColor: darkBlue,
                              opacity: isDragging || fields.length - 1 === index ? 0 : 0.25,
                            }}
                          ></div>
                        </div>*/}
                      </li>
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>

        {relevantChallenges.length > fields.length && (
          <Box display="flex" justifyContent="center">
            <IconButton title={t`Add`} aria-label={t`Add`} onClick={handleAdd} style={{ borderRadius: '10px' }}>
              <AddRoundedIcon
                style={{
                  width: 24,
                  height: 24,
                }}
              />
              <span
                style={{
                  marginLeft: isRtl ? 0 : '10px',
                  marginRight: !isRtl ? 0 : '10px',
                  fontSize: '0.9375rem',
                  color: darkBlue,
                }}
              >{t`Add challenge`}</span>
            </IconButton>
          </Box>
        )}
      </DragDropContext>
    </>
  ) : (
    <div
      style={{ textAlign: 'center' }}
    >{t`The selected period does not currently have any wellness points challenges. You can create and add them later.`}</div>
  )
}
